import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput,
  NumberInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

interface EpxWellMgmtData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  mode: string;
  step: number;
  askDiet: boolean;
  hasDietPlan: boolean;
  trackDietDays: boolean;
  askExercise: boolean;
  trackExerciseMinutes: boolean;
  hasStepDevice: boolean;
  hasScale: boolean;
  height?: number;
  isSmoker: boolean;
  hasHealthCoach: boolean;
  areTipsSeparated: boolean;
  askBloodSugar: boolean;
  askBloodPressure: boolean;
}

type EpxWellMgmtDataRaw = DateSerialized<EpxWellMgmtData, 'start' | 'end'>;

export const EpxWellMgmtDefaultData: EpxWellMgmtData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  mode: 'W',
  step: 1,
  days: [3],
  askDiet: true,
  hasDietPlan: false,
  trackDietDays: false,
  askExercise: true,
  trackExerciseMinutes: false,
  hasStepDevice: false,
  hasScale: true,
  height: null,
  isSmoker: false,
  hasHealthCoach: false,
  areTipsSeparated: true,
  askBloodSugar: false,
  askBloodPressure: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxWellMgmtFormProps {
  setForm?: (form: FormInstance<EpxWellMgmtDataRaw>) => void;
  onSubmit?: (data: EpxWellMgmtData) => void;
  isDisabled?: boolean;
}

const EpxWellMgmtForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxWellMgmtFormProps): JSX.Element => {
  const serialize = (data: EpxWellMgmtDataRaw): EpxWellMgmtData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      height: data.hasScale ? data.height : null
    };
  };
  const deserialize = (data: EpxWellMgmtData): EpxWellMgmtDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxWellMgmtDefaultData);
  const form = new FormInstance<EpxWellMgmtDataRaw, EpxWellMgmtData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxWellMgmtData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBreakout>
                <Callout intent="primary" className="mb-6">
                  The optional second day chosen will serve as a reminder day in
                  case the first session for the week wasn't finished.
                </Callout>
                <Weekday name="days" minItems={1} maxItems={2} inline />
              </CardBreakout>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Diet"
                      name="askDiet"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Has Diet Plan"
                      name="hasDietPlan"
                      fill
                      disabled={!props.values.askDiet}
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Track Exact Days of Diet"
                      name="trackDietDays"
                      fill
                      disabled={!props.values.askDiet}
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Exercise"
                      name="askExercise"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Track Exact Minutes of Exercise"
                      name="trackExerciseMinutes"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Has Step Counter"
                      name="hasStepDevice"
                      fill
                      disabled={!props.values.askExercise}
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Weight (Scale Required)"
                      name="hasScale"
                      fill
                    />
                  </Col>
                  {props.values.hasScale && (
                    <Col sm={12}>
                      <NumberInput label="Height (inches)" name="height" fill />
                    </Col>
                  )}
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Smoking"
                      name="isSmoker"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Has Health Coach"
                      name="hasHealthCoach"
                      fill
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Use Module Specific Tips"
                      name="areTipsSeparated"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxWellMgmtForm;
